import React, { useRef, useEffect, useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import Datetime from 'react-datetime';
import moment, { Moment } from 'moment';

type DatetimeProps = Datetime['props'];

/**
 * Wrapper around react-datetime that renders the calendar picker into a
 * fixed-position portal on document.body so it escapes both overflow clipping
 * and stacking-context limitations from ancestor containers.
 *
 * Drop-in replacement: accepts all the same props as <Datetime />.
 */
const DatetimePortal: React.FC<DatetimeProps> = (props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const openedAtRef = useRef(0);

    const [currentValue, setCurrentValue] = useState<Moment | string | undefined>(() => {
        const initial = props.value ?? props.defaultValue;
        if (!initial) return undefined;
        if (moment.isMoment(initial)) return initial;
        return moment.utc(initial);
    });

    const positionAndShow = useCallback(() => {
        const container = containerRef.current;
        const portal = portalRef.current;
        if (!container || !portal) return;

        const input = container.querySelector('input');
        if (!input) return;

        const rect = input.getBoundingClientRect();
        const top = rect.bottom + 1;
        const left = rect.left;

        portal.style.position = 'fixed';
        portal.style.zIndex = '99999';
        portal.style.backgroundColor = '#fff';
        portal.style.top = `${top}px`;
        portal.style.left = `${left}px`;
        portal.style.display = 'block';
        portal.style.visibility = 'hidden';

        requestAnimationFrame(() => {
            if (!portal || portal.style.display === 'none') return;

            const pickerEl = portal.querySelector('.rdtPicker') as HTMLElement | null;
            if (!pickerEl) {
                portal.style.visibility = 'visible';
                return;
            }

            const pickerHeight = pickerEl.offsetHeight;
            const pickerWidth = pickerEl.offsetWidth;

            const finalTop = (top + pickerHeight > window.innerHeight)
                ? rect.top - pickerHeight - 1
                : top;

            const finalLeft = (left + pickerWidth > window.innerWidth)
                ? Math.max(0, window.innerWidth - pickerWidth)
                : left;

            portal.style.top = `${finalTop}px`;
            portal.style.left = `${finalLeft}px`;
            portal.style.visibility = 'visible';
        });
    }, []);

    const hide = useCallback(() => {
        const portal = portalRef.current;
        if (portal) {
            portal.style.display = 'none';
            portal.style.visibility = 'hidden';
        }
    }, []);

    const handleOpen = useCallback(() => {
        // Ignore duplicate focus events (react-datetime fires focus twice)
        if (Date.now() - openedAtRef.current < 200) return;

        openedAtRef.current = Date.now();
        setIsOpen(true);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                positionAndShow();
            });
        });
    }, [positionAndShow]);

    const handleChange = useCallback((value: Moment | string) => {
        setCurrentValue(value);
        setIsOpen(false);
        hide();
        if (props.onChange) {
            props.onChange(value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are complete; props.onChange is the only prop used
    }, [props.onChange, hide]);

    const close = useCallback(() => {
        setIsOpen(false);
        hide();
    }, [hide]);

    // Close on click/tap outside the portal picker
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e: MouseEvent | TouchEvent) => {
            if (Date.now() - openedAtRef.current < 100) return;

            const portal = portalRef.current;
            const container = containerRef.current;
            const target = e.target as Node;

            if (portal && portal.contains(target)) return;
            if (container && container.contains(target)) return;

            close();
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen, close]);

    // Close on scroll of any ancestor (fixed picker would be stranded)
    useEffect(() => {
        if (!isOpen) return;

        const handleScroll = (e: Event) => {
            if (Date.now() - openedAtRef.current < 300) return;

            const portal = portalRef.current;
            if (portal && e.target instanceof Node && portal.contains(e.target)) return;

            close();
        };

        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [isOpen, close]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentional discards to build restProps without these
    const { onChange: _onChange, onFocus: _onFocus, defaultValue: _dv, value: _v, ...restProps } = props;
    const valueProps = currentValue ? { value: currentValue } : {};

    // Portal is always mounted but hidden via display:none — no mount/unmount flash
    const portalPicker = ReactDOM.createPortal(
        <div
            ref={portalRef}
            id="datetime-portal-overlay"
            style={{ display: 'none', visibility: 'hidden', position: 'fixed', zIndex: 99999 }}
        >
            <Datetime
                {...restProps}
                {...valueProps}
                open={isOpen}
                input={false}
                onChange={handleChange}
            />
        </div>,
        document.body
    );

    return (
        <div ref={containerRef} className="datetime-portal-wrapper">
            <Datetime
                {...restProps}
                {...valueProps}
                open={false}
                onChange={handleChange}
                onFocus={handleOpen}
            />
            {portalPicker}
        </div>
    );
};

export default DatetimePortal;
